import { useState, useEffect, useMemo, useRef } from 'react';
import { Skeleton, Select, SelectItem } from '@nextui-org/react';
import LineChart from '../../../../common/src/components/charts/LineChart';
import useDataStore from '../../../../common/src/stores/useDataStore';
import useFilterStore from '../../../../common/src/stores/useFiltersStore';
import {
  DashboardSeasonComparisonRow,
  getDashboardSeasonComparison,
} from 'common/src/api/market_data';
import {
  DEFAULT_ORIGIN,
  RMB_TO_USD,
} from '../../../../common/src/utils/definitions';
import { getCurrentSeason } from '../../../../common/src/utils/functions';
import {
  DEFAULT_SEASON_WEEKS,
  fillSeasonWeekGaps,
  getSeasonWeekPosition,
  getSeasonWeeks,
} from '../../../../common/src/utils/seasonWeekOrder';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import ChartDownloadButton from '../../../../common/src/components/charts/ChartDownloadButton';

/**
 * Rangos de la ventana móvil, en meses y su equivalente en semanas.
 * `maxLabels` topea las etiquetas del eje: hasta 13 semanas entran todas en el
 * ancho del widget; de ahí para arriba se ralean o quedan ilegibles.
 */
const RANGE_OPTIONS = [
  { months: 2, weeks: 8 },
  { months: 3, weeks: 13 },
  { months: 6, weeks: 26, maxLabels: 13 },
  { months: 12, weeks: 52, maxLabels: 13 },
];
const DEFAULT_RANGE_MONTHS = 2;

const SeasonComparisonWidget = () => {
  const { t } = useTranslation('dashboard');
  const { selectedSpecies } = useDataStore();
  const { selectedSeason } = useFilterStore();
  const [currentWeek, setCurrentWeek] = useState<number>(0);
  const [selectedVariety, setSelectedVariety] = useState<string>('all');
  const [rangeMonths, setRangeMonths] = useState<number>(DEFAULT_RANGE_MONTHS);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [comparisonRows, setComparisonRows] = useState<
    DashboardSeasonComparisonRow[]
  >([]);
  const [topVarieties, setTopVarieties] = useState<string[]>([]);
  // Por defecto solo Chile, como en Mercado Evolutivo: mezclar con la fruta
  // del hemisferio norte deforma la curva. 'all' = todos los orígenes.
  const [selectedOrigin, setSelectedOrigin] = useState<string>(DEFAULT_ORIGIN);
  const [origins, setOrigins] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCurrency] = useState<'RMB' | 'USD'>('RMB');
  const [priceView] = useState<string>('kg');

  // Calculate previous season dynamically
  const previousSeason = useMemo(() => {
    if (!selectedSeason) return '';
    const [startYear, endYear] = selectedSeason.split('-').map(Number);
    return `${startYear - 1}-${endYear - 1}`;
  }, [selectedSeason]);

  // Al cambiar de especie se vuelve al origen por defecto.
  useEffect(() => {
    setSelectedOrigin(DEFAULT_ORIGIN);
  }, [selectedSpecies?.id]);

  // Fetch data for both seasons
  useEffect(() => {
    if (!selectedSpecies?.id || !selectedSeason || !previousSeason) {
      setComparisonRows([]);
      setTopVarieties([]);
      setLoading(false);
      return;
    }

    const fetchSeasonData = async () => {
      setLoading(true);
      try {
        const data = await getDashboardSeasonComparison(
          selectedSpecies.id,
          selectedSeason,
          previousSeason,
          selectedOrigin === 'all' ? null : selectedOrigin
        );

        const availableOrigins = data?.origins || [];
        // Si la especie no tiene precios de ese origen (palta, naranja), se
        // cae a todos en vez de dejar el gráfico vacío.
        if (
          selectedOrigin !== 'all' &&
          availableOrigins.length > 0 &&
          !availableOrigins.includes(selectedOrigin)
        ) {
          setSelectedOrigin('all');
          return;
        }

        setOrigins(availableOrigins);
        setCurrentWeek(data?.current_week || 0);
        setComparisonRows(data?.rows || []);
        setTopVarieties(data?.top_varieties || []);
      } catch (error) {
        console.error('Error fetching season data:', error);
        setOrigins([]);
        setCurrentWeek(0);
        setComparisonRows([]);
        setTopVarieties([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSeasonData();
  }, [selectedSpecies?.id, selectedSeason, previousSeason, selectedOrigin]);

  // Process data for chart
  const chartData = useMemo(() => {
    if (!selectedVariety || !selectedSeason || !previousSeason) {
      return { series: [], categories: [] };
    }

    const filterData = (season: string) => {
      return comparisonRows.filter(
        (item) => item.season === season && item.variety === selectedVariety
      );
    };

    const mapWeeklyMedian = (data: DashboardSeasonComparisonRow[]) => {
      const weeklyData: { [key: number]: number } = {};

      data.forEach((item) => {
        const weekNum = Number(item.week);
        if (
          !Number.isFinite(weekNum) ||
          weekNum < 1 ||
          weekNum > 53 ||
          !Number.isFinite(item.median_price_kg)
        ) {
          return;
        }

        let price = item.median_price_kg;
        if (selectedCurrency === 'USD') {
          price = RMB_TO_USD(price);
        }
        const multiplier =
          priceView === 'kg' ? 1 : parseInt(priceView.split('-')[1]);
        price *= multiplier;

        weeklyData[weekNum] = price;
      });

      return weeklyData;
    };

    const currentData = filterData(selectedSeason);
    const previousData = filterData(previousSeason);

    const currentWeeklyData = mapWeeklyMedian(currentData);
    const previousWeeklyData = mapWeeklyMedian(previousData);

    // Get all available weeks from both seasons
    const allWeeks = new Set<number>();
    Object.keys(currentWeeklyData).forEach((week) =>
      allWeeks.add(parseInt(week))
    );
    Object.keys(previousWeeklyData).forEach((week) =>
      allWeeks.add(parseInt(week))
    );

    if (allWeeks.size === 0) {
      return { series: [], categories: [] };
    }

    // El dashboard es un resumen: se muestran las últimas WINDOW_WEEKS semanas
    // contadas desde la última con datos de la temporada actual. El análisis de
    // la temporada completa vive en el Mercado Evolutivo.
    const currentSeasonWeeks = Object.keys(currentWeeklyData).map(Number);
    // En la temporada en curso, o si la temporada todavía no tiene datos, la
    // ventana termina en la semana del último dato de mercado (current_week).
    // No se usa la semana "más tardía" con datos: la temporada puede partir a
    // fines de junio (semana 26), que en el orden 27→26 queda al final.
    const isOngoingSeason = selectedSeason === getCurrentSeason();
    // La semana 53 entra solo si alguna de las dos temporadas la tiene.
    const comparedSeasons = [selectedSeason, previousSeason];
    const seasonWeeks = allWeeks.has(53)
      ? DEFAULT_SEASON_WEEKS
      : getSeasonWeeks(comparedSeasons);
    const lastPosition =
      currentWeek > 0 && (isOngoingSeason || !currentSeasonWeeks.length)
        ? seasonWeeks.indexOf(currentWeek)
        : currentSeasonWeeks.length
          ? Math.max(...currentSeasonWeeks.map((w) => seasonWeeks.indexOf(w)))
          : -1;

    // Las semanas intermedias sin datos quedan como hueco, no se colapsan.
    const windowWeeks =
      RANGE_OPTIONS.find((r) => r.months === rangeMonths)?.weeks ??
      RANGE_OPTIONS[0].weeks;

    const windowSlice =
      lastPosition >= 0
        ? seasonWeeks.slice(
            Math.max(0, lastPosition - (windowWeeks - 1)),
            lastPosition + 1
          )
        : fillSeasonWeekGaps(Array.from(allWeeks), comparedSeasons);
    // El eje parte en la primera semana del rango con datos de alguna de las
    // dos temporadas (ej. Chile recién parte en la semana 42).
    const firstWithData = windowSlice.findIndex((week) => allWeeks.has(week));
    const sortedWeeks =
      firstWithData > 0 ? windowSlice.slice(firstWithData) : windowSlice;

    const categories = sortedWeeks.map((weekNum) => `W${weekNum}`);

    // Map data to categories (fill with null for weeks without data)
    const mapToCategories = (
      weeklyData: { [key: number]: number },
      maxWeek?: number
    ) => {
      return sortedWeeks.map((weekNum) => {
        // If maxWeek is specified, check if we should show data for this week
        if (maxWeek !== undefined) {
          // Comparación en orden de temporada
          if (getSeasonWeekPosition(weekNum) > getSeasonWeekPosition(maxWeek)) {
            return { x: `W${weekNum}`, y: null };
          }
        }

        const prices = weeklyData[weekNum];
        if (prices === undefined) {
          return { x: `W${weekNum}`, y: null };
        }

        return {
          x: `W${weekNum}`,
          y: parseFloat(prices.toFixed(2)),
        };
      });
    };

    // Generate series with same color, different tones
    const baseHue = 220; // Blue hue
    const varietyLabel =
      selectedVariety === 'all' ? t('Todas') : selectedVariety;
    const series = [
      {
        name: `${varietyLabel} (${previousSeason})`,
        data: mapToCategories(previousWeeklyData),
        color: `hsl(${baseHue}, 70%, 70%)`, // Lighter tone
        type: 'line' as const,
      },
      {
        name: `${varietyLabel} (${selectedSeason})`,
        data: mapToCategories(currentWeeklyData), // No limit - show all available data
        color: `hsl(${baseHue}, 70%, 40%)`, // Darker tone
        type: 'line' as const,
      },
    ];

    return { series, categories };
  }, [
    currentWeek,
    comparisonRows,
    selectedVariety,
    selectedSeason,
    previousSeason,
    selectedCurrency,
    priceView,
    rangeMonths,
  ]);

  if (loading) {
    return (
      <div className='space-y-4'>
        <Skeleton className='h-8 w-1/3 rounded-lg' />
        <Skeleton className='h-64 w-full rounded-lg' />
      </div>
    );
  }

  if (!selectedSeason || !previousSeason || comparisonRows.length === 0) {
    return (
      <div className='text-center p-8'>
        <p className='text-default-400'>
          {t('No hay datos disponibles para comparar temporadas')}
        </p>
      </div>
    );
  }

  if (topVarieties.length === 0) {
    return (
      <div className='text-center p-8'>
        <p className='text-default-400'>
          {t('No hay variedades disponibles en los datos')}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className='flex flex-wrap justify-between items-center mb-4 gap-4'>
        <div className='flex min-w-max flex-1 flex-col'>
          <div className='flex items-center gap-2'>
            <h3 className='whitespace-nowrap text-lg font-semibold'>
              {t('Tendencia vs Temporada Anterior')}
            </h3>
            <ChartDownloadButton
              chartRef={chartContainerRef}
              title={t('Tendencia vs Temporada Anterior')}
              subtitle={`${selectedSeason} vs ${previousSeason}`}
            />
          </div>
          <span className='text-sm text-default-400'>
            {selectedSeason} vs {previousSeason}
          </span>
        </div>

        <div className='flex w-full md:w-auto gap-2'>
          <Select
            label={t('Rango')}
            selectedKeys={[String(rangeMonths)]}
            onChange={(e) =>
              setRangeMonths(Number(e.target.value) || DEFAULT_RANGE_MONTHS)
            }
            className='w-full md:w-36'
            size='sm'
            aria-label={t('Selecciona rango')}
          >
            {RANGE_OPTIONS.map((option) => (
              <SelectItem key={String(option.months)} value={String(option.months)}>
                {`${option.months} ${t('meses')}`}
              </SelectItem>
            ))}
          </Select>

          <Select
            label={t('Origen')}
            selectedKeys={[selectedOrigin]}
            disallowEmptySelection
            onChange={(e) => setSelectedOrigin(e.target.value || 'all')}
            className='w-full md:w-36'
            size='sm'
            aria-label={t('Selecciona origen')}
          >
            {[
              <SelectItem key='all' value='all'>
                {t('Todos')}
              </SelectItem>,
              ...origins.map((origin) => (
                <SelectItem key={origin} value={origin}>
                  {origin}
                </SelectItem>
              )),
            ]}
          </Select>

          <Select
            label={t('Variedad')}
            selectedKeys={selectedVariety ? [selectedVariety] : []}
            onChange={(e) => setSelectedVariety(e.target.value)}
            className='w-full md:w-48'
            size='sm'
            aria-label={t('Selecciona variedad')}
          >
            <SelectItem key='all' value='all'>
              {t('Todas')}
            </SelectItem>
            {topVarieties.map((variety) => (
              <SelectItem key={variety} value={variety}>
                {variety}
              </SelectItem>
            ))}
          </Select>
        </div>
      </div>

      {chartData.series.length > 0 ? (
        <div ref={chartContainerRef}>
          <LineChart
            series={chartData.series}
            xaxisCategories={chartData.categories}
            // La temporada anterior va punteada; la actual, continua
            strokeDashArray={[6, 0]}
            maxXLabels={
              RANGE_OPTIONS.find((r) => r.months === rangeMonths)?.maxLabels
            }
            yaxisTitle={`${t('Precio')} ${selectedCurrency} / ${
              priceView === 'kg' ? 'Kg' : '5kg box'
            }`}
            selectedCurrency={selectedCurrency}
            priceView={priceView as 'kg' | 'box'}
            viewMode='week'
          />
        </div>
      ) : (
        <div className='text-center p-8 text-default-400'>
          {t('No hay datos disponibles para la variedad seleccionada')}
        </div>
      )}
    </div>
  );
};

export default SeasonComparisonWidget;
